/**
 * Content Input Feature Module
 *
 * Provides components and utilities for the bot content input experience:
 * - ContentInputArea: Large multiline input for text content (up to 50K chars)
 * - FileUploadZone: Drag-and-drop file upload for documents and images
 * - FilePreviewList: Display uploaded files with extraction preview
 * - ImagePreview: Display uploaded images with processing status
 * - ProcessingIndicator: Loading state during bot creation
 * - useContentInput: Form state management hook with React Hook Form + Zod
 * - contentInputSchema: Zod validation schema for content
 * - Image validation utilities for size and count limits
 *
 * @example
 * ```tsx
 * import { ContentInputArea, FileUploadZone, ProcessingIndicator } from '@/features/content-input';
 *
 * // In your component:
 * const { isPending } = useCreateTempBot();
 * return isPending ? <ProcessingIndicator /> : <ContentInputArea onSubmit={...} />;
 * ```
 */

export { ContentInputArea, type ContentInputAreaProps } from './ContentInputArea';
export {
  FileUploadZone,
  type FileUploadZoneProps,
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE,
} from './FileUploadZone';
export {
  FilePreviewList,
  type FilePreviewListProps,
  type UploadedFile,
  type FileStatus,
  type ProcessingMethod,
} from './FilePreviewList';
export {
  ImagePreview,
  ImagePreviewList,
  type ImagePreviewProps,
  type ImagePreviewListProps,
  type UploadedImage,
  type ImageStatus,
} from './ImagePreview';
export { ProcessingIndicator, type ProcessingIndicatorProps } from './ProcessingIndicator';
export { UrlInputField, type UrlInputFieldProps } from './UrlInputField';
export {
  UrlPreviewCard,
  UrlPreviewList,
  type UrlPreviewCardProps,
  type UrlPreviewListProps,
  type ExtractedUrl,
  type UrlStatus,
} from './UrlPreviewCard';
export { UrlCrawlProgress, type UrlCrawlProgressProps } from './UrlCrawlProgress';
export {
  GoogleDocPreviewCard,
  GoogleDocPreviewList,
  type GoogleDocPreviewCardProps,
  type GoogleDocPreviewListProps,
  type GoogleDocContent,
  type GoogleDocStatus,
} from './GoogleDocPreviewCard';
export { useContentInput, type UseContentInputReturn } from './useContentInput';
export {
  useContentSources,
  type UseContentSourcesReturn,
} from './useContentSources';
export {
  ContentSourceList,
  type ContentSourceListProps,
} from './ContentSourceList';
export {
  ContentSourceItem,
  type ContentSourceItemProps,
  type DragHandleProps,
} from './ContentSourceItem';
export {
  SortableSourceList,
  type SortableSourceListProps,
} from './SortableSourceList';
export {
  ContentSummary,
  type ContentSummaryProps,
} from './ContentSummary';
export {
  ContentLimitWarning,
  type ContentLimitWarningProps,
} from './ContentLimitWarning';
export {
  UnifiedContentPanel,
  type UnifiedContentPanelProps,
} from './UnifiedContentPanel';
export {
  LeftSidebarSourcesList,
  type LeftSidebarSourcesListProps,
} from './LeftSidebarSourcesList';
export {
  CollapsibleUploadPreview,
  type CollapsibleUploadPreviewProps,
} from './CollapsibleUploadPreview';
export {
  SidebarProgressSection,
  type SidebarProgressSectionProps,
} from './SidebarProgressSection';
export {
  contentInputSchema,
  MAX_CONTENT_LENGTH,
  MAX_FILE_SIZE as MAX_FILE_SIZE_VALIDATION,
  MAX_IMAGE_SIZE,
  MAX_IMAGE_COUNT,
  SUPPORTED_FILE_TYPES,
  SUPPORTED_FILE_EXTENSIONS,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_IMAGE_EXTENSIONS,
  validateFile,
  validateFiles,
  isImageFile,
  validateImage,
  validateImageCount,
  validateImages,
  createPastedImageFile,
  isGoogleDocUrl,
  getGoogleDocType,
  type ContentInputData,
  type FileValidationErrorCode,
  type FileValidationResult,
  type ImageValidationErrorCode,
  type ImageValidationResult,
} from './validation';
